import { useState } from 'react';

import { IconAngleLeft } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { NEW_IwLink } from '@iwRouter/NEW_IwLink';
import {
    useStripe,
    useElements,
    CardNumberElement
} from '@stripe/react-stripe-js';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import {
    AddPaymentCardFormData,
    AddPaymentCardForm
} from './AddPaymentCardForm';
import {
    AddPaymentPreferencesForm,
    AddPaymentPreferencesFormData
} from './AddPaymentPreferencesForm';
import { useGetPaymentCardData } from './useGetPaymentData';
import { ONBOARDING_PATHS } from '../../constants';

export const AddPaymentMethodForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [paymentPreferencesData, setPaymentPreferencesData] =
        useState<AddPaymentPreferencesFormData | null>(null);
    const [paymentCardData, setPaymentCardData] =
        useState<AddPaymentCardFormData | null>(null);

    const { submitPaymentPreference, registerDebitCard } =
        useGetPaymentCardData();

    const handleSaveAndReturn = async () => {
        if (!paymentPreferencesData || !paymentCardData) {
            setError('Both forms must be completed');
            return;
        }
        setIsSubmitting(true);
        setError(null);

        try {
            // Only create Stripe payment method if adding a new card (billing postcode is provided)
            const isAddingNewCard = Boolean(paymentCardData.billingPostcode);

            if (isAddingNewCard) {
                if (!stripe || !elements) {
                    setError('Stripe has not loaded yet');
                    return;
                }

                const cardElement = elements.getElement(CardNumberElement);
                if (!cardElement) {
                    throw new Error('Card element not found');
                }

                await registerDebitCard({
                    stripe,
                    cardNumberElement: cardElement,
                    billingPostcode: paymentCardData.billingPostcode,
                    cardholderName: '' // Empty string as cardholder name is not captured in form
                });
            }

            // Submit payment preferences to the API
            const paymentAmount =
                paymentPreferencesData.paymentPreference === 'fixed_amount'
                    ? Number(paymentPreferencesData.fixedAmount)
                    : undefined;

            await submitPaymentPreference({
                paymentPreference: paymentPreferencesData.paymentPreference,
                paymentAmount
            });

            // Navigate back after successful submission
            history.push(
                maintainStateKey({
                    pathname: ONBOARDING_PATHS.ONBOARDING_CHECKS
                })
            );
        } catch (error) {
            console.error('Error submitting forms:', error);
            setError(
                error instanceof Error
                    ? error.message
                    : 'An error occurred while submitting'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="my-2xl">
            <NEW_IwLink to={{ pathname: ONBOARDING_PATHS.ONBOARDING_CHECKS }}>
                <Text className="mb-xl flex flex-row gap-xs text-s font-bold text-primary-40">
                    <IconAngleLeft className="my-auto" />
                    <FormattedMessage
                        id="_.credit_card.onboarding.payment_method.back_link"
                        defaultMessage="Back to list"
                    />
                </Text>
            </NEW_IwLink>

            <Text className="text-3xl font-bold">
                <FormattedMessage
                    id="_.credit_card.onboarding.payment_method.title"
                    defaultMessage="Set up your payments"
                />
            </Text>

            <Text className="my-m text-xl font-reg">
                <FormattedMessage
                    id="_.credit_card.onboarding.payment_method.description"
                    defaultMessage="Add a debit card to use for repayments and tell us how you'd like to repay each month."
                />
            </Text>

            <div className="space-y-xl">
                <AddPaymentPreferencesForm
                    onFormChange={setPaymentPreferencesData}
                />

                <AddPaymentCardForm onFormChange={setPaymentCardData} />

                {error && <Text className="text-s text-error">{error}</Text>}

                <div className="flex justify-center">
                    <Button
                        onClick={handleSaveAndReturn}
                        disabled={isSubmitting}
                        className="md:w-auto w-full px-2xl"
                    >
                        <FormattedMessage
                            id="_.credit_card.onboarding.payment_method.submit_button"
                            defaultMessage="Save and return"
                        />
                    </Button>
                </div>
            </div>
        </div>
    );
};
